import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import {
  Bouton, Carte, Champ, Message, Selecteur, ZoneTexte,
} from '@journal/ui';
import { ApiError, creerClientApi } from '../lib/api.ts';
import { PiedDePage } from '../components/PiedDePage.tsx';
import { ADRESSE_CONTACT, ADRESSE_CONTACT_ABOUTIT } from '../lib/contact.ts';

/**
 * Contact public — la porte de secours.
 *
 * ── Elle n'est pas le support ───────────────────────────────────────────────
 * Le support vit DANS l'application, où l'on sait qui écrit et ce qu'il paie.
 * Cette page-ci sert à ceux que rien d'autre n'atteint : le compte payé dont la
 * validation n'a pas abouti, l'adresse saisie de travers qui ne reçoit ni la
 * vérification ni la réinitialisation, l'espace supprimé au terme du délai.
 *
 * Le « mot de passe oublié » couvre le cas courant, et il faut d'ailleurs le
 * dire ici : renvoyer quelqu'un vers un formulaire qui attendra, quand un lien
 * automatique le débloquerait en trente secondes, est un mauvais service.
 *
 * ── Elle n'exige aucune session ─────────────────────────────────────────────
 * Le client API est créé sans jeton. C'est toute la raison d'être de la page :
 * exiger un compte de quelqu'un qui écrit parce qu'il n'en a pas serait une
 * boucle fermée.
 */

const CATEGORIES = [
  { valeur: 'paiement', texte: 'Un problème de paiement' },
  { valeur: 'acces', texte: 'Je n’arrive pas à accéder à mon espace' },
  { valeur: 'autre', texte: 'Autre' },
] as const;

export function Contact() {
  const [categorie, setCategorie] = useState<string>('acces');
  const [email, setEmail] = useState('');
  const [sujet, setSujet] = useState('');
  const [corps, setCorps] = useState('');
  const [leurre, setLeurre] = useState('');

  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoye, setEnvoye] = useState(false);

  async function envoyer(evenement: FormEvent) {
    evenement.preventDefault();
    setEnvoi(true);
    setErreur(null);
    try {
      const api = creerClientApi();
      await api.requete('/public/contact', {
        methode: 'POST',
        corps: {
          email: email.trim(),
          category: categorie,
          subject: sujet.trim(),
          body: corps.trim(),
          site: leurre,
        },
      });
      setEnvoye(true);
    } catch (e) {
      /*
       * ── Trois échecs, trois phrases ────────────────────────────────────
       * « L'envoi n'a pas abouti » couvrait tout, et renvoyait vers une
       * adresse qui, tant qu'elle n'est pas renseignée, n'aboutit nulle part.
       * On fermait ainsi la dernière porte de quelqu'un qui n'en avait plus
       * d'autre.
       *
       * La limite de débit se dit à part : c'est le seul cas où réessayer tout
       * de suite ne sert à rien, et le seul où l'on peut annoncer un délai.
       */
      const statut = e instanceof ApiError ? e.status : 0;
      if (statut === 400) {
        /*
         * ── Le refus que la page CACHAIT ────────────────────────────────
         * Un message trop court était rejeté par le serveur, et la page
         * répondait « L'envoi n'a pas abouti » — une panne, en somme. On
         * réessayait donc à l'identique, indéfiniment, sans jamais apprendre
         * que trois mots ne suffisent pas.
         *
         * Le message du serveur n'est pas repris tel quel : il est écrit en
         * anglais, pour un développeur. Celui-ci dit la même chose à qui
         * remplit le formulaire.
         */
        setErreur(
          'Votre message est trop court. Décrivez en quelques phrases ce que '
          + 'vous faisiez et ce qui s’est passé : c’est ce qui permet de vous '
          + 'répondre utilement du premier coup.',
        );
      } else if (statut === 429) {
        setErreur(
          'Plusieurs messages ont déjà été envoyés depuis cet appareil. '
          + 'Attendez une heure avant d’en écrire un nouveau : les précédents '
          + 'sont bien arrivés, inutile de les répéter.',
        );
      } else if (ADRESSE_CONTACT_ABOUTIT) {
        setErreur(
          `L’envoi n’a pas abouti. Réessayez dans un moment, ou écrivez `
          + `directement à ${ADRESSE_CONTACT}.`,
        );
      } else {
        setErreur(
          'L’envoi n’a pas abouti. Rien n’a été enregistré : réessayez dans un '
          + 'moment, votre message n’est pas perdu à moitié.',
        );
      }
    } finally {
      setEnvoi(false);
    }
  }

  return (
    <div className="min-h-dvh bg-[var(--color-bg)]">
      <div className="mx-auto max-w-[46rem] px-5 py-12">
        <Link
          to="/offres"
          className="text-[14px] text-[var(--color-fg-muted)] hover:text-[var(--color-fg)]"
        >
          ← Journal de Classe
        </Link>

        <h1 className="mt-6 text-[28px] font-semibold tracking-tight">
          Nous écrire
        </h1>
        <p className="mt-2 text-[15px] leading-relaxed text-[var(--color-fg-muted)]">
          Un problème de paiement, ou d’accès à votre espace.
        </p>

        {envoye ? (
          <div className="mt-8">
            <Message ton="succes" titre="Message reçu">
              <p>
                Il est enregistré et vous sera répondu à l’adresse que vous avez
                indiquée. Vérifiez vos indésirables : c’est là qu’atterrissent
                les réponses aux messages de ce type.
              </p>
              <p className="mt-2">
                <Link to="/offres" className="underline">
                  Retourner à la présentation
                </Link>
              </p>
            </Message>
          </div>
        ) : (
          <>
            <Carte className="mt-6">
              <form onSubmit={envoyer} className="flex flex-col gap-4">
                {erreur && <Message ton="erreur">{erreur}</Message>}

                <Selecteur
                  libelle="De quoi s’agit-il ?"
                  value={categorie}
                  onChange={(e) => setCategorie(e.target.value)}
                  options={CATEGORIES.map((c) => ({ valeur: c.valeur, texte: c.texte }))}
                />

                <Champ
                  libelle="Votre adresse électronique"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  aide="Celle où vous pouvez lire la réponse, pas nécessairement celle du compte, si c’est justement elle qui pose problème."
                  required
                />

                <Champ
                  libelle="Sujet"
                  value={sujet}
                  onChange={(e) => setSujet(e.target.value)}
                  aide="En quelques mots."
                  required
                  minLength={3}
                />

                <ZoneTexte
                  libelle="Votre message"
                  value={corps}
                  onChange={(e) => setCorps(e.target.value)}
                  /*
                   * Le même rappel que dans le support, et pour la même raison :
                   * ce texte transite par un courriel et se conserve. Il est ici
                   * d'autant plus nécessaire que l'expéditeur n'est pas
                   * authentifié.
                   */
                  aide={
                    corps.trim().length > 0 && corps.trim().length < 10
                      ? 'Quelques phrases, au minimum : « ça ne marche pas » ne permet pas de vous répondre.'
                      : 'Le nom de votre école aide à vous retrouver. N’écrivez pas de nom d’élève.'
                  }
                  rows={7}
                  required
                  /*
                   * La même borne que le serveur, dite AVANT l'envoi.
                   *
                   * Le navigateur refuse alors de soumettre et pointe le champ
                   * en cause : plus utile qu'un aller-retour réseau qui revient
                   * en « requête invalide », et qui laissait le texte saisi
                   * sans indiquer quoi corriger.
                   */
                  minLength={10}
                />

                {/*
                  * Leurre anti-robots. Masqué à l'œil ET aux lecteurs d'écran,
                  * et exclu du parcours au clavier : un champ qu'un lecteur
                  * d'écran annoncerait ferait échouer précisément les gens
                  * qu'on ne veut pas gêner.
                  */}
                <div aria-hidden="true" className="absolute left-[-9999px]">
                  <label htmlFor="site">Ne rien écrire ici</label>
                  <input
                    id="site"
                    name="site"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={leurre}
                    onChange={(e) => setLeurre(e.target.value)}
                  />
                </div>

                <div className="flex justify-end">
                  <Bouton type="submit" variante="primary" chargement={envoi}>
                    Envoyer
                  </Bouton>
                </div>
              </form>
            </Carte>

            {/*
              * Proposée seulement si elle ABOUTIT : offrir une adresse
              * réservée reviendrait à indiquer une porte murée.
              */}
            {ADRESSE_CONTACT_ABOUTIT ? (
              <p className="mt-4 text-[13px] leading-relaxed text-[var(--color-fg-muted)]">
                {'Vous préférez écrire depuis votre messagerie ? '}
                <a href={`mailto:${ADRESSE_CONTACT}`} className="underline">
                  {ADRESSE_CONTACT}
                </a>
              </p>
            ) : (
              <p className="mt-4 text-[13px] leading-relaxed">
                <span className="rounded bg-[var(--color-warning-soft)] px-1.5 py-0.5 font-medium text-[var(--color-warning)]">
                  À COMPLÉTER : adresse électronique de contact
                </span>
              </p>
            )}
          </>
        )}

        {/*
          * ── Le lien vers la présentation, en bas ────────────────────────────
          * Cette page est publique et finira indexée : quelqu'un peut y arriver
          * sans savoir ce qu'est le produit. Une ligne qui le dit, et un lien
          * vers la présentation — c'est le seul endroit du produit où une
          * phrase de réclame a sa place, parce qu'elle répond à une question
          * que le lecteur se pose vraiment.
          */}
        <section className="mt-12 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5">
          <h2 className="text-[16px] font-semibold tracking-tight">
            Journal de Classe
          </h2>
          <p className="mt-1.5 text-[14px] leading-relaxed text-[var(--color-fg-muted)]">
            Le cahier journal, les progressions et l’emploi du temps d’une classe
            de primaire, au même endroit, avec le programme officiel intégré, et
            des données hébergées en France qui s’exportent à tout moment.
          </p>
          <p className="mt-3">
            <Link to="/offres" className="text-[14px] font-medium underline">
              Voir ce que fait l’application
            </Link>
          </p>
        </section>

        <PiedDePage />
      </div>
    </div>
  );
}
